<template>
    <div
        :class="componentClasses"
        @click="hideNotification"
        @keydown.esc="hideNotification"
    >
        <div class="notification-block__prefix">
            <svg-icon
                :icon="accepted ? CheckIcon : BellOnIcon"
                is-custom-icon
                color="white"
            />
        </div>
        <div class="notification-block__content">
            <template v-if="!accepted">
                <text-decorator
                    v-if="props.title"
                    class="notification-block__title"
                    bold
                >
                    {{ props.title }}
                </text-decorator>
                {{ props.text ? props.text : '' }}
            </template>
            <template v-else>
                <text-decorator
                    class="notification-block__title"
                    bold
                >
                    Dit chatverzoek is aangenomen
                </text-decorator>
                <p style="line-height: 16px">
                    <template v-if="name">
                        Door: <span style="font-weight: 800;">{{ name }}</span><br><br>
                    </template>
                    Het bericht sluit nu automatisch!
                </p>
            </template>
        </div>
        <div class="notification-block__button">
            <button type="button" @click="handleClick">
                Chat openen
            </button>
        </div>
    </div>
</template>

<script lang="ts" setup>
    import { computed, onMounted, ref } from 'vue';
    import { useRender } from '@keyvue/one-ui';
    import CheckIcon from '@keyvue/one-awesome-icons/icons/regular/check.svg?component';
    import BellOnIcon from '@keyvue/one-awesome-icons/icons/regular/bell-on.svg?component';
    import { useRouter } from 'vue-router';
    import InboxFolder from '@/ai/assistent/types/InboxFolder';
    import MercureMessageEvent from '@/services/keyvue-api/mercure/MercureMessageEvent';
    import useEventBus from '@/services/eventbus/composables/useEventBus';
    import { Chat } from '@keyvue/one-api-client';

    export interface Props {
        title: string
        text?: string
        chatId: string
        assistentId: string
    }

    const eventBus = useEventBus();
    const router = useRouter();
    const props = withDefaults(defineProps<Props>(), {
        text: '',
    });

    const accepted = ref(false);
    const name = ref<string | null>(null);

    onMounted(() => {
        eventBus.subscribe(MercureMessageEvent('company', 'chat.handover-accepted'), onChatHandoverAccepted);
    });

    function onChatHandoverAccepted(event) {
        const chat = Chat.fromObject(event);

        if (chat.id.id !== props.chatId) {
            return;
        }

        accepted.value = true;
        name.value = chat.handoverAcceptedBy?.name.fullName ?? null;

        setTimeout(unRender.value, 5000);
    }

    async function handleClick() {
        router.push({
            name: 'assistent-detail-folder',
            params: {
                assistentId: props.assistentId,
                inbox: InboxFolder.CONVERSATIONS.routeSlug,
                id: props.chatId,
            },
        });
        unRender.value();
    }

    function hideNotification() {
        unRender.value();
    }

    const {
        rendered,
        setUnRender,
        unRender,
        unRendered,
        unRenderDelay,
        onRender,
        onUnRender,
    } = useRender(200);

    const componentClasses = computed(() => ({
        'notification-block': true,
        'notification-block--accepted': accepted.value,
        'notification-block--rendered': rendered.value,
        'notification-block--un-rendered': unRendered.value,
    }));

    defineExpose({
        setUnRender,
        onRender,
        onUnRender,
        unRenderDelay: unRenderDelay.value,
    });
</script>

<style lang="scss" scoped>
.notification-block {
    @include box-shadow-360();

    pointer-events: auto;
    cursor: pointer;

    transform: translateX(50px);

    display: flex;
    flex-wrap: wrap;
    gap: size(sm);

    box-sizing: border-box;
    width: 100%;
    padding: size(md);

    font-size: font-size(xs);
    color: var(--theme-colors-default--gray-800);

    opacity: 0;
    background-color: var(--theme-colors-default--white);
    border-bottom: 2px solid var(--theme-colors-default--red-400);
    border-radius: size(xs);

    transition: transform .2s, opacity .2s, border-color .2s;

    &__content {
        display:flex;
        flex-direction: column;
        gap: size(xs);
        justify-content: center;
        width: calc(100% - 30px - size(sm));
    }

    &__title {
        color: var(--theme-colors-default--red-400);
        transition: color .2s;
    }

    &__prefix {
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: size(xs);
        height: 30px;
        width: 30px;
        background-color: var(--theme-colors-default--red-400);
        transition: background-color .2s;
    }

    &__button {
        display: flex;
        justify-content: flex-end;
        padding-top: size(sm);
        width: 100%;

        & > button {
            background-color: var(--theme-colors-default--green-500);
            border: 0;
            padding: 5px 10px;
            color: var(--theme-colors-default--white);
            font-weight: bold;
            border-radius: size(xs);
            border: 1px solid var(--theme-colors-default--green-500);

            &:hover {
                background-color: var(--theme-colors-default--white);
                color: var(--theme-colors-default--green-500);
            }
        }
    }

    &--space-between {
        justify-content: space-between;
    }

    &--rendered {
        transform: translateX(0);
        opacity: 1;
    }

    &--un-rendered {
        transform: translateX(50px);
        opacity: 0;
    }

    &--accepted {
        border-color: var(--theme-colors-default--green-500);

        & .notification-block__prefix {
            background-color: var(--theme-colors-default--green-500);
        }
        & .notification-block__title {
            color: var(--theme-colors-default--green-500);
        }
    }
}
</style>
